import { UserPlacementGroup } from '@zappar/zappar-react-three-fiber/lib/index-standalone';
import { ReactElement, FunctionComponent, memo, useEffect, useRef, useState } from 'react';
import { WorldTrackerQuality } from '@zappar/zappar-threejs/lib/index-standalone';
import { usePlacementBehaviour } from '../trackerGroupHooks';
import { DoubleSide, Group } from 'three';
import { useOutlineGlow, usePlaneLostBehaviour, useShowInitializationUI } from './hooks';
import WorldTrackingPlacementUI, { IWorldTrackingMode } from './WorldTrackingPlacementUI/WorldTrackingPlacementUI';
import PlacementScene from '../PlacementScene';
import { useCameraIsUserFacing, useGetCurrentSceneId } from '@/hooks/general';
import ShadowPlane from '@r3f/components/ShadowPlane';
import { Jute } from '@/index';
import { useThree } from '@react-three/fiber';
import dotMaterial from './dotMaterial';
import { useSceneStore } from '@/zustand';
import { useGetPreviousTrackingType } from '@/components/hoc/hocCustomHooks';
import PlacementScenes from '../PlacementScenes';

// setLogLevel(LogLevel.LOG_LEVEL_VERBOSE);

type IParentProps = {
	enabled: boolean;
	children: ReactElement[] | undefined;
	onShowContentFirstTime?: () => unknown;
};

const WorldTrackerWrapper: FunctionComponent<IParentProps> = ({ enabled, children }) => {
	const trackerContentGroupRef = useRef<Group>(null);
	const trackerChildGroupRef = useRef<Group>(null);
	const innerGroupRef = useRef<Group | undefined>(undefined);
	const [mode, setMode] = useState<IWorldTrackingMode>('initializing');
	const { exitPlacementBehaviour, enterPlacementBehaviour } = usePlacementBehaviour();
	const { exitPlaneLostBehaviour, enterPlaneLostBehaviour } = usePlaneLostBehaviour(trackerContentGroupRef, trackerChildGroupRef, true);
	const currentSceneId = useGetCurrentSceneId();
	const showInitializationUi = useShowInitializationUI(mode === 'initializing');
	const hasIntersectedSinceEnabledRef = useRef(false);
	const isSceneSetup = useRef(false);
	const isLastModePlaced = useRef(false);
	const isCameraUserFacing = useCameraIsUserFacing();
	const isIntersecting = mode === 'intersecting' || mode === 'intersecting-no-animation';
	const isSearching = mode === 'searching' || mode === 'searching-no-animation';
	const { scene } = useThree();
	const lastEventWasIntersectionFoundRef = useRef(false);
	const lastEventWasIntersectionNotFoundRef = useRef(false);
	const placementMode = useSceneStore(({ placementMode }) => placementMode);
	const previousTrackingType = useGetPreviousTrackingType();
	const [localEnabled, setLocalEnabled] = useState(true);

	const isNavigatedFromFaceTracking = enabled && previousTrackingType === 'face';
	const disableAutoPlacement = Jute.isMobileWebXRExperience && isNavigatedFromFaceTracking;

	useOutlineGlow(enabled && !Jute.isMobileWebXRExperience, mode);

	useEffect(() => {
		if (!Jute.isMobileWebXRExperience) return;
		dotMaterial.visible = false;
	}, []);

	useEffect(() => {
		if (!Jute.isMobileWebXRExperience) {
			setLocalEnabled(true);
			return;
		}

		if (!enabled) setLocalEnabled(false)
		else {
			setTimeout(() => {
				setLocalEnabled(true);
			}, previousTrackingType === 'face' ? 500 : 0)
		}
	}, [enabled])

	useEffect(() => {
		const arScene = scene.getObjectByName('ArScene');
		if (!arScene) return;
		const userPlacmementGroup = arScene.getObjectByName('userPlacementGroup');
		if (!userPlacmementGroup) return;
		const planeMeshGroup = userPlacmementGroup.children[0];
		if (planeMeshGroup) planeMeshGroup.renderOrder = -Infinity;
	}, [mode]);

	const onPlaneIntersectionFound = (disableAnimation: boolean) => {
		if (mode === 'initializing' || mode === 'placed') return;
		hasIntersectedSinceEnabledRef.current = true;
		enterPlacementBehaviour();
		exitPlaneLostBehaviour(disableAnimation);
	};

	const onPlaneIntersectionNotFound = (disableAnimation: boolean) => {
		if (mode === 'initializing' || mode === 'placed') return;
		enterPlacementBehaviour();
		enterPlaneLostBehaviour(disableAnimation);
	};

	useEffect(() => {
		if (!enabled) {
			isSceneSetup.current = false;
			isLastModePlaced.current = mode === 'placed';
			return;
		}
		if (isLastModePlaced.current && !disableAutoPlacement) {
			setMode('placed');
			isSceneSetup.current = true;
			exitPlacementBehaviour();
			return;
		}
		setTimeout(() => {
			enterPlacementBehaviour();
			if (mode !== 'initializing') setMode(lastEventWasIntersectionFoundRef.current ? 'intersecting-no-animation' : 'searching-no-animation');
			isSceneSetup.current = true;
		}, Jute.isMobileWebXRExperience && previousTrackingType === 'face' ? 500 : 0)
	}, [enabled]);

	useEffect(() => {
		if (!enabled) return;
		switch (mode) {
			case 'intersecting':
				onPlaneIntersectionFound(false);
				break;
			case 'intersecting-no-animation':
				onPlaneIntersectionFound(true);
				break;
			case 'searching':
				onPlaneIntersectionNotFound(false);
				break;
			case 'searching-no-animation':
				onPlaneIntersectionNotFound(true);
				break;
			case 'placed':
				exitPlacementBehaviour();
				break;
		}
	}, [mode]);

	return (
		<>
			<WorldTrackingPlacementUI
				enabled={enabled && localEnabled && mode !== 'initializing'}
				mode={mode}
				onPlace={() => setMode('placed')}
				onReset={() => {
					setMode(lastEventWasIntersectionFoundRef.current ? 'intersecting-no-animation' : 'searching-no-animation');
				}}
			/>
			<UserPlacementGroup
				enabled={enabled && localEnabled && (Jute.isAppClip || Jute.isMobileWebXRExperience)}
				name="userPlacementGroup"
				placementMode={mode !== 'placed'}
				startInPlacementMode
				placeOnPlanes
				showContentDuringPlacement
				featurePoints={{ show: true }}
				planeMaterial={dotMaterial}
				showPlaceButton={false}
				showInitializationUI={showInitializationUi && enabled}
				showRestartPlacementButton={false}
				onPlaneIntersection={() => {
					if (lastEventWasIntersectionFoundRef.current || !placementMode) return;
					lastEventWasIntersectionFoundRef.current = true;
					lastEventWasIntersectionNotFoundRef.current = false;
					if (mode === 'intersecting-no-animation' || !isSceneSetup.current) return;
					setMode('intersecting');
				}}
				onPlaneIntersectionEnd={() => {
					if (lastEventWasIntersectionNotFoundRef.current || !placementMode) return;
					lastEventWasIntersectionNotFoundRef.current = true;
					if (mode === 'searching-no-animation' || !isSceneSetup.current) return;
					setMode(mode !== 'initializing' ? 'searching' : 'searching-no-animation');
					lastEventWasIntersectionFoundRef.current = false;
				}}
				onPlacementModeChange={(isPlacementMode: boolean) => {
					// placementMode is exited without placementMode prop change
					if (isPlacementMode && mode === 'placed' && enabled && localEnabled) {
						// below triggers setting a new mode (intersecting/non-intersecting)
						enterPlacementBehaviour();
						setMode(lastEventWasIntersectionFoundRef.current ? 'intersecting-no-animation' : 'searching-no-animation');
					}
				}}
				onWorldTrackerQualityChange={(q: WorldTrackerQuality) => {
					if (q === WorldTrackerQuality.WORLD_TRACKER_QUALITY_INITIALIZING) {
						if (enabled && localEnabled && !isCameraUserFacing) {
							setMode('initializing');
						}
						if (!innerGroupRef.current) innerGroupRef.current = trackerChildGroupRef.current?.parent as Group | undefined;
						return;
					}
					innerGroupRef.current = trackerChildGroupRef.current?.parent as Group | undefined;
					if (innerGroupRef.current) innerGroupRef.current.name = 'wt-inner-group';

					if (isLastModePlaced.current && !disableAutoPlacement) setMode('placed')
					else if (mode === 'initializing') setMode(lastEventWasIntersectionFoundRef.current ? 'intersecting-no-animation' : 'searching-no-animation');
				}}
			>
				<group ref={trackerChildGroupRef} />
				<group name="trackerContentGroup" ref={trackerContentGroupRef} scale={1} visible={enabled && localEnabled}>
					<group visible={enabled && localEnabled && (isIntersecting || isSearching) && !isCameraUserFacing}>
						<PlacementScenes />
						<mesh visible={mode === 'intersecting' || mode === 'intersecting-no-animation'} rotation={[Math.PI / 2, 0, 0]}>
							<ringGeometry args={[0.25, 0.28, 50]} />
							<meshBasicMaterial color={0xffffff} side={DoubleSide} />
						</mesh>
					</group>
					<group visible={enabled && localEnabled && mode === 'placed'}>{children}</group>
				</group>
				{enabled && localEnabled && mode === 'placed' && <ShadowPlane rotation={[-Math.PI / 2, 0, 0]} occlusion opacity={0.3} />}
			</UserPlacementGroup>
		</>
	);
};

WorldTrackerWrapper.displayName = 'WorldTrackerWrapper';
export default memo(WorldTrackerWrapper);
